import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { GestionService } from "../../../services/gestion.service";
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { Message, SpinnerService } from "shared-components";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";

@Component({
  selector: "app-mrs-new-edit",
  templateUrl: "./new-edit.component.html",
})
export class MrsNewEditComponent implements OnInit {
  private _marca;
  public newEditForm: UntypedFormGroup;
  public showError: boolean;
  public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
  public mensajes = new Array<Message>();
  public etiquetas = [];
  public disenyoCajas = [];
  public disenyoPallets = [];
  public grupoCajas;
  public grupoPallets;
  public toCloseSpinner = 0;

  @Input() marcaId;
  @Output() refresh = new EventEmitter<boolean>();
  @Output() mensaje = new EventEmitter<string>();

  constructor(
    private _fb: UntypedFormBuilder,
    private readonly _spinnerService: SpinnerService,
    private readonly _dataProvider: DataProvider,
    private readonly _gestionService: GestionService,
    private readonly _sessionContext: SessionContextService,
  ) {}

  ngOnInit() {
    this.newEditForm = this._fb.group({
      nombre: ["", Validators.required],
      alias: [""],
      etiquetaCaja: [""],
      etiquetaPallet: [""],
      grupoCajas: [""],
      grupoPallets: [""],
      marcaIntegracion: [""],
    });

    this._spinnerService.showSpinner();
    const promises: Promise<any>[] = [];
    promises.push(this.getDisenyosEtiquetas());
    promises.push(this.getGruposEtiquetas());

    Promise.all(promises)
      .then(
        () => {
          if (this.marcaId) {
            this.getMarca();
          }
          return true;
        },
        (error) => {
          console.log("Promise rejected with " + JSON.stringify(error));
        }
      )
      .catch((e) => console.log(e));
  }

  public get isSmallScreen() {
    return this._sessionContext.isSmallScreen();
  }

  private getMarca() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._gestionService
      .getMarca(this.marcaId)
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        this._marca = resp;
        this.patchValue(this._marca);
      }, (error) => {
        this.showApiError = true;
        if (error.status === 420) {
          this.mensajes.push({
            class: "error",
            message: "global.error.noAccess",
          });
        } else {
          this.mensajes.push({
            class: "error",
            message: "global.error.noResponse",
          });
        }
      });
  }

  private patchValue(marca) {
    this.newEditForm.patchValue({
      nombre: marca.des,
      alias: marca.alias,
      marcaIntegracion: marca.marcaIntegracionId,
      etiquetaCaja: this.disenyoCajas.find(
        (x) => x.id === marca.disenyoEtiquetaIdCaja
      ),
      etiquetaPallet: this.disenyoPallets.find(
        (x) => x.id === marca.disenyoEtiquetaIdPallet
      ),
      grupoCajas: this.grupoCajas.find(
        (x) => x.id === marca.grupoEtiquetaIdCaja
      ),
      grupoPallets: this.grupoPallets.find(
        (x) => x.id === marca.grupoEtiquetaIdPallet
      ),
    });
  }

  private getDisenyosEtiquetas(): Promise<any> {
    return new Promise((resolve, reject) => {
      let filters = {};
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._gestionService
        .getDisenyoEtiquetas(filters)
        .toPromise()
        .finally(() => {
          this.checkCloseSpinner();
        })
        .then((resp) => {
          this.disenyoCajas = resp.lista.filter(
            (x) => x.tipoConfeccionId === 1
          );
          this.disenyoPallets = resp.lista.filter(
            (x) => x.tipoConfeccionId === 2
          );
          setTimeout(resolve, 1);
        });
    });
  }

  private getGruposEtiquetas(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._gestionService
        .getGrupoEtiquetas()
        .toPromise()
        .finally(() => {
          this.checkCloseSpinner();
        })
        .then((resp) => {
          this.grupoCajas = resp.filter((x) => x.tipoConfeccionId === 1);
          this.grupoPallets = resp.filter((x) => x.tipoConfeccionId === 2);
          setTimeout(resolve, 1);
        });
    });
  }

  public close() {
    this.refresh.emit(false);
  }

  public save() {
    this.checkForm();
    if (this.newEditForm.valid && !this.marcaId) {
      this._marca = {
        des: this.newEditForm.get("nombre").value
          ? this.newEditForm.get("nombre").value
          : "",
        alias: this.newEditForm.get("alias").value
          ? this.newEditForm.get("alias").value
          : "",
        disenyoEtiquetaIdCaja: this.newEditForm.get("etiquetaCaja").value
          ? this.newEditForm.get("etiquetaCaja").value.id
          : "",
        disenyoEtiquetaIdPallet: this.newEditForm.get("etiquetaPallet").value
          ? this.newEditForm.get("etiquetaPallet").value.id
          : "",
        grupoEtiquetaIdCaja: this.newEditForm.get("grupoCajas").value
          ? this.newEditForm.get("grupoCajas").value.id
          : "",
        grupoEtiquetaIdPallet: this.newEditForm.get("grupoPallets").value
          ? this.newEditForm.get("grupoPallets").value.id
          : "",
      };
      this._spinnerService.showSpinner();
      this._gestionService
        .createMarca(this._marca)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then((resp) => {
          if (resp.id) {
            this._dataProvider.refreshMarcas();
            this.refresh.emit(true);
            this.mensaje.emit("global.success.itemCreated");
          } else {
            this.showError = true;
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          }
        });
    } else if (this.newEditForm.valid && this.marcaId) {
      this._marca = {
        id: this.marcaId,
        des: this.newEditForm.get("nombre").value
          ? this.newEditForm.get("nombre").value
          : "",
        alias: this.newEditForm.get("alias").value
          ? this.newEditForm.get("alias").value
          : "",
        disenyoEtiquetaIdCaja: this.newEditForm.get("etiquetaCaja").value
          ? this.newEditForm.get("etiquetaCaja").value.id
          : "",
        disenyoEtiquetaIdPallet: this.newEditForm.get("etiquetaPallet").value
          ? this.newEditForm.get("etiquetaPallet").value.id
          : "",
        grupoEtiquetaIdCaja: this.newEditForm.get("grupoCajas").value
          ? this.newEditForm.get("grupoCajas").value.id
          : "",
        grupoEtiquetaIdPallet: this.newEditForm.get("grupoPallets").value
          ? this.newEditForm.get("grupoPallets").value.id
          : "",
      };
      this._spinnerService.showSpinner();
      this._gestionService
        .updateMarca(this._marca)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then(
          (resp) => {
            this._dataProvider.refreshMarcas();
            this.refresh.emit(true);
            this.mensaje.emit("global.success.itemUpdated");
          },
          (error) => {
            this.showError = true;
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          }
        );
    }
  }

  private checkForm() {
    Object.keys(this.newEditForm.controls).forEach((key) => {
      if (
        this.newEditForm.controls[key].status == "DISABLED" &&
        !this.newEditForm.controls[key].pristine
      ) {
        this.newEditForm.setErrors({ invalid: true });
        this.showError = true;
        this.mensajes.push({
          class: "error",
          message:
            "Detectado intento de uso malicioso de la aplicación. Incidencia registrada. La empresa se pondrá en contacto con el usuario de la incidencia.",
        });
        return;
      }
    });

    this.showError = this.newEditForm.invalid;
    if (this.showError) {
      this.mensajes.push({ class: "error", message: "global.error.required" });
    }
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
      this._spinnerService.hideSpinner();
    }
  }
}
